import { useCallback } from "react";
import { type FacetValue } from "../../../../models";
import FilterGroupItem from "../../FilterGroupItem";

import { Checkbox, FormControlLabel, List, ListItem, styled, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { C_ON_SURFACE_LOW_EMPHASIS } from "~/common/components/icon/Colors";
import type { FacetFilterData } from "~/Search/filterConfigModels";
import { useDispatch, useSelector } from "react-redux";
import type { RootState } from "~/redux/store";
import { toggleFacet } from "~/Search/redux/actions";
import { selectFacetValueByCode } from "~/Search/redux/searchSelectors";

interface ReduxFacetFilterProps {
  data: FacetFilterData;
  hidden?: boolean;
}

export const FacetFilter = ({ data, hidden }: ReduxFacetFilterProps) => {
  const facetValues = useSelector((state: RootState) => selectFacetValueByCode(state, data.code));
  const dispatch = useDispatch();

  return (
    <BaseFacetFilter
      data={data}
      values={facetValues.values}
      onToggleFacet={(facetValueCode, facetCategoryCode) => dispatch(toggleFacet(facetValueCode, facetCategoryCode))}
      hidden={hidden}
    />
  );
};

interface Props {
  data: FacetFilterData;
  values: FacetValue[];
  onToggleFacet: (facetValueCode: string, facetCategoryCode: string) => void;
  hidden?: boolean;
}

const BaseFacetFilter = (props: Props) => {
  const { title, initiallyExpanded, code: facetCategoryCode } = props.data;
  const { values, onToggleFacet: externalOnToggle } = props;

  const onToggleFacet = useCallback(
    (facetCode: string) => {
      externalOnToggle(facetCode, facetCategoryCode);
    },
    [externalOnToggle, facetCategoryCode],
  );

  const toExpand = initiallyExpanded || props.values.find((facetValue) => facetValue.selected) != undefined;
  return (
    <FilterGroupItem title={title} initiallyExpanded={toExpand} data-test-id={props.data.code} hidden={props.hidden}>
      <List>
        {values.map((value) => (
          <StyledListItem key={value.code}>
            <FacetFilterValue key={value.code} {...value} onToggle={onToggleFacet} />
          </StyledListItem>
        ))}
      </List>
    </FilterGroupItem>
  );
};

const StyledListItem = styled(ListItem)(() => ({
  minHeight: "40px",
}));

interface FacetFilterValueProps extends FacetValue {
  onToggle: (facetCode: string) => void;
}

const FacetFilterValue = ({ code, onToggle, count, name, selected }: FacetFilterValueProps) => {
  const cb = useCallback(() => {
    onToggle(code);
  }, [code, onToggle]);
  const { t } = useTranslation();
  const translatedName = t(name);

  const label = (
    <Typography variant={"labelMedium"} component={"span"}>
      {translatedName}
      <Counter> ({count})</Counter>
    </Typography>
  );

  return (
    <FormControlLabel
      sx={{ marginBlock: "-8px" }}
      control={<Checkbox checked={selected} onChange={cb} data-test-id={`facet-filter-${code}`} />}
      label={label}
    />
  );
};

const Counter = styled("span")({
  color: C_ON_SURFACE_LOW_EMPHASIS,
  whiteSpace: "pre",
});
